import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject, catchError, forkJoin, of, switchMap } from 'rxjs';
import { DialogComponent } from '@/app/atoms/dialog/dialog.component';
import { AdminBrandingEmailsService } from '../../services/admin-branding-emails.service';
import {
  EmailCatalogItem,
  EmailCatalogPreview,
  EmailCatalogTab,
} from './emails.types';

@Component({
  selector: 'admin-branding-emails',
  templateUrl: './emails.html',
  styleUrl: './emails.scss',
  imports: [DialogComponent],
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Emails implements OnInit, OnDestroy {
  private readonly emailsService = inject(AdminBrandingEmailsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly reload$ = new Subject<void>();

  readonly loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  readonly loadError = signal<string | null>(null);
  readonly tabs = signal<EmailCatalogTab[]>([]);
  readonly activeTabId = signal('');
  readonly previews = signal<Record<string, EmailCatalogPreview>>({});
  readonly previewLoading = signal(false);
  readonly copiedId = signal<string | null>(null);
  readonly previewOpen = signal(false);
  readonly selectedEmail = signal<EmailCatalogItem | null>(null);

  private blobUrls = new Map<string, string>();
  private copiedResetTimer: ReturnType<typeof setTimeout> | null = null;

  readonly activeTab = computed(() => {
    const id = this.activeTabId();
    const tabs = this.tabs();
    return tabs.find((tab) => tab.id === id) ?? tabs[0] ?? null;
  });

  readonly selectedPreview = computed(() => {
    const email = this.selectedEmail();
    return email ? this.previews()[email.id] ?? null : null;
  });

  constructor() {
    this.reload$
      .pipe(
        switchMap(() => {
          this.loadingState.set('loading');
          this.loadError.set(null);
          return this.emailsService.getEmailCatalog().pipe(
            catchError((err: unknown) => {
              this.loadError.set(this.readErrorMessage(err, 'Failed to load email catalog.'));
              this.loadingState.set('error');
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((library) => {
        if (!library) {
          return;
        }
        this.tabs.set(library.tabs);
        const current = this.activeTabId();
        const stillExists = library.tabs.some((tab) => tab.id === current);
        const nextTabId = stillExists ? current : (library.tabs[0]?.id ?? '');
        this.activeTabId.set(nextTabId);
        this.loadingState.set('loaded');
        this.loadPreviewsForTab(nextTabId);
      });
  }

  ngOnInit(): void {
    this.loadCatalog();
  }

  ngOnDestroy(): void {
    if (this.copiedResetTimer) {
      clearTimeout(this.copiedResetTimer);
    }
    for (const url of this.blobUrls.values()) {
      URL.revokeObjectURL(url);
    }
    this.blobUrls.clear();
  }

  loadCatalog(): void {
    this.clearPreviewCache();
    this.reload$.next();
  }

  private clearPreviewCache(): void {
    for (const url of this.blobUrls.values()) {
      URL.revokeObjectURL(url);
    }
    this.blobUrls.clear();
    this.previews.set({});
  }

  tabLabel(tab: EmailCatalogTab): string {
    return `${tab.label} (${tab.emails.length})`;
  }

  selectTab(tabId: string): void {
    this.activeTabId.set(tabId);
    this.loadPreviewsForTab(tabId);
  }

  isTabSelected(tabId: string): boolean {
    return this.activeTabId() === tabId;
  }

  getTabClass(tabId: string): string {
    return this.isTabSelected(tabId)
      ? 'border-zinc-900 bg-zinc-900 text-white dark:border-zinc-100 dark:bg-zinc-100 dark:text-zinc-900'
      : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-300 dark:hover:border-zinc-600 dark:hover:bg-zinc-900';
  }

  styleLabel(style: string): string {
    if (style === 'branded') {
      return 'Branded';
    }
    if (style === 'legacy') {
      return 'Legacy';
    }
    return 'Simple';
  }

  iframeUrl(emailId: string): SafeResourceUrl | null {
    const html = this.previews()[emailId]?.html;
    if (!html) {
      return null;
    }
    let url = this.blobUrls.get(emailId);
    if (!url) {
      url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      this.blobUrls.set(emailId, url);
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  openPreview(email: EmailCatalogItem): void {
    this.selectedEmail.set(email);
    this.previewOpen.set(true);
    if (!this.previews()[email.id]) {
      this.loadPreviewsForTab(this.activeTabId());
    }
  }

  closePreview(): void {
    this.previewOpen.set(false);
    this.selectedEmail.set(null);
  }

  async copySubject(email: EmailCatalogItem): Promise<void> {
    try {
      await navigator.clipboard.writeText(email.subject);
      if (this.copiedResetTimer) {
        clearTimeout(this.copiedResetTimer);
      }
      this.copiedId.set(email.id);
      this.copiedResetTimer = setTimeout(() => {
        this.copiedId.set(null);
        this.copiedResetTimer = null;
      }, 1600);
    } catch {
      this.copiedId.set(null);
    }
  }

  isCopied(email: EmailCatalogItem): boolean {
    return this.copiedId() === email.id;
  }

  private loadPreviewsForTab(tabId: string): void {
    const tab = this.tabs().find((item) => item.id === tabId);
    if (!tab) {
      return;
    }

    const cached = this.previews();
    const missing = tab.emails.filter((email) => !cached[email.id]);
    if (missing.length === 0) {
      return;
    }

    this.previewLoading.set(true);
    forkJoin(
      missing.map((email) =>
        this.emailsService.getEmailPreview(email.id).pipe(
          catchError(() => of(null)),
        ),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((results) => {
        const next = { ...this.previews() };
        for (const preview of results) {
          if (!preview) {
            continue;
          }
          const existing = this.blobUrls.get(preview.id);
          if (existing) {
            URL.revokeObjectURL(existing);
            this.blobUrls.delete(preview.id);
          }
          next[preview.id] = preview;
        }
        this.previews.set(next);
        this.previewLoading.set(false);
      });
  }

  private readErrorMessage(err: unknown, fallback: string): string {
    if (!err || typeof err !== 'object') {
      return fallback;
    }

    const httpErr = err as {
      message?: string;
      error?: { message?: string | string[] };
      status?: number;
      statusText?: string;
    };

    const nested = httpErr.error?.message;
    if (Array.isArray(nested) && nested.length > 0) {
      return nested.join(', ');
    }
    if (typeof nested === 'string' && nested.trim()) {
      return nested;
    }
    if (typeof httpErr.message === 'string' && httpErr.message.trim() && !httpErr.message.startsWith('Http failure')) {
      return httpErr.message;
    }
    if (httpErr.status && httpErr.statusText) {
      return `${httpErr.status} ${httpErr.statusText}`;
    }
    return fallback;
  }
}
